import { css } from '@design.estate/dees-element';

/**
 * Shared, token-based styles for foss.global view components.
 *
 * Ported from the Claude Design `foss.global` prototype (styles/app.css), with the
 * design's `--dees-color-*` tokens mapped onto the short token names that
 * `dotglobal-app` defines on its `:host` and that cascade through the shadow tree
 * (`--accent`, `--text-primary`, `--border-default`, ...). Components import this so
 * the base layer (layout utils, buttons, badges, cards, form fields, auth + settings
 * chrome) lives in one place instead of being duplicated per element.
 */
export const dotglobalSharedStyles = css`
  :host {
    /* San Francisco system stack — re-declared here so components that import
       these shared styles resolve the same fonts the app sets on its host. */
    --font-sans: -apple-system, BlinkMacSystemFont, 'SF Pro Text', 'SF Pro Display', 'Helvetica Neue', Helvetica, Arial, sans-serif;
    --font-display: -apple-system, BlinkMacSystemFont, 'SF Pro Display', 'SF Pro Text', 'Helvetica Neue', Helvetica, sans-serif;
    --font-mono: ui-monospace, 'SF Mono', 'SFMono-Regular', Menlo, Monaco, 'Cascadia Code', 'Liberation Mono', monospace;
  }

  * {
    box-sizing: border-box;
  }
  a {
    color: inherit;
    text-decoration: none;
  }
  button {
    border: 0;
    background: none;
    color: inherit;
    cursor: pointer;
    font: inherit;
  }
  input,
  textarea {
    font: inherit;
  }
  dees-icon {
    display: inline-flex;
    flex-shrink: 0;
  }

  /* ---------- layout utils ---------- */
  .page {
    width: 100%;
    max-width: var(--maxw, 1180px);
    margin: 0 auto;
    padding: 0 24px;
  }
  .content {
    padding-top: 24px;
    padding-bottom: 56px;
  }
  .row {
    display: flex;
    align-items: center;
  }
  .stack {
    display: flex;
    flex-direction: column;
  }
  .grow {
    flex: 1;
    min-width: 0;
  }
  .wrap {
    flex-wrap: wrap;
  }
  .gap4 { gap: 4px; }
  .gap6 { gap: 6px; }
  .gap8 { gap: 8px; }
  .gap10 { gap: 10px; }
  .gap12 { gap: 12px; }
  .gap14 { gap: 14px; }
  .gap16 { gap: 16px; }
  .gap20 { gap: 20px; }
  .gap24 { gap: 24px; }
  .muted { color: var(--text-muted); }
  .sec { color: var(--text-secondary); }
  .mono { font-family: var(--font-mono); font-feature-settings: 'tnum'; }
  .display { font-family: var(--font-display); }
  .nowrap { white-space: nowrap; }
  .divider { height: 1px; background: var(--border-default); }
  .sectitle { font-family: var(--font-display); font-weight: 700; letter-spacing: -0.01em; }

  /* ---------- avatar ---------- */
  .avatar {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    border-radius: 50%;
    background: var(--accent);
    color: #fff;
    font-weight: 600;
    font-family: inherit;
    overflow: hidden;
    user-select: none;
  }
  .avatar.sq { border-radius: 6px; }
  .avatar img {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  /* ---------- buttons ---------- */
  .btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 7px;
    height: 34px;
    padding: 0 14px;
    border-radius: 6px;
    font-size: 13px;
    font-weight: 500;
    letter-spacing: -0.01em;
    white-space: nowrap;
    border: 1px solid var(--border-strong);
    background: var(--bg-tertiary);
    color: var(--text-primary);
    transition: background 0.15s, border-color 0.15s, color 0.15s, box-shadow 0.15s;
    cursor: pointer;
  }
  .btn:hover { background: var(--hover); }
  .btn.sm { height: 30px; padding: 0 11px; font-size: 12px; gap: 6px; }
  .btn.lg { height: 40px; padding: 0 18px; font-size: 14px; }
  .btn.primary { background: var(--accent); border-color: var(--accent); color: var(--accent-fg); }
  .btn.primary:hover { background: color-mix(in srgb, var(--accent) 88%, #000); }
  .btn.ghost { background: transparent; border-color: transparent; color: var(--text-secondary); }
  .btn.ghost:hover { background: var(--hover); color: var(--text-primary); }
  .btn.danger {
    color: var(--error);
    border-color: color-mix(in srgb, var(--error) 40%, var(--border-default));
    background: transparent;
  }
  .btn.danger:hover { background: color-mix(in srgb, var(--error) 12%, transparent); }
  .btn:disabled { opacity: 0.5; cursor: not-allowed; }
  .btn .ico { opacity: 0.85; }
  .btn.provider { height: 42px; padding: 0 12px; font-size: 13.5px; gap: 10px; width: 100%; }

  /* ---------- badges ---------- */
  .badge {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 2px 8px;
    font-size: 11.5px;
    font-weight: 500;
    line-height: 1.5;
    border-radius: 999px;
    border: 1px solid transparent;
    white-space: nowrap;
  }
  .badge.sq { border-radius: 4px; }
  .badge.def { background: var(--bg-tertiary); color: var(--text-secondary); }
  .badge.success { background: color-mix(in srgb, var(--success) 18%, transparent); color: var(--success); }
  .badge.warning { background: color-mix(in srgb, var(--warning) 20%, transparent); color: var(--warning); }
  .badge.error { background: color-mix(in srgb, var(--error) 18%, transparent); color: var(--error); }
  .badge.accent { background: color-mix(in srgb, var(--accent) 16%, transparent); color: var(--accent); }
  .badge.outline { border-color: var(--border-strong); color: var(--text-secondary); }

  /* ---------- cards ---------- */
  .card {
    background: var(--bg-primary);
    border: 1px solid var(--border-default);
    border-radius: 8px;
    overflow: hidden;
  }
  .card-h {
    display: flex;
    align-items: center;
    gap: 10px;
    height: 44px;
    padding: 0 16px;
    border-bottom: 1px solid var(--border-default);
    font-size: 13px;
    font-weight: 600;
  }

  /* ---------- form fields ---------- */
  .fld {
    display: flex;
    flex-direction: column;
    gap: 6px;
    flex: 1;
    min-width: 170px;
  }
  .fld label { font-size: 12px; font-weight: 600; color: var(--text-secondary); }
  .fld input,
  .fld textarea {
    width: 100%;
    padding: 8px 11px;
    font-size: 13px;
    background: var(--bg-tertiary);
    border: 1px solid var(--border-default);
    border-radius: 6px;
    color: var(--text-primary);
    outline: none;
    resize: vertical;
    transition: border-color 0.15s, box-shadow 0.15s;
  }
  .fld input::placeholder,
  .fld textarea::placeholder { color: var(--text-muted); }
  .fld input:focus,
  .fld textarea:focus {
    border-color: var(--accent);
    box-shadow: 0 0 0 3px color-mix(in srgb, var(--accent) 18%, transparent);
  }
  .fld input:disabled { opacity: 0.6; cursor: not-allowed; }
  .fld input.mono,
  .fld textarea.mono { font-family: var(--font-mono); font-size: 12.5px; }

  /* ---------- agent glyph + spinner ---------- */
  .agent-glyph {
    width: 40px;
    height: 40px;
    flex-shrink: 0;
    border-radius: 6px;
    background: color-mix(in srgb, var(--accent) 18%, transparent);
    color: var(--accent);
    display: flex;
    align-items: center;
    justify-content: center;
  }
  @keyframes dg-spin { to { transform: rotate(360deg); } }
  .spin { animation: dg-spin 0.9s linear infinite; }

  /* ---------- auth ---------- */
  .auth { display: flex; align-items: flex-start; justify-content: center; }
  .authgrid {
    display: grid;
    grid-template-columns: minmax(280px, 430px) minmax(310px, 400px);
    gap: 64px;
    padding-top: 34px;
    align-items: start;
  }
  @media (max-width: 880px) {
    .authgrid { grid-template-columns: minmax(0, 420px); gap: 28px; padding-top: 12px; }
  }
  .authcard { padding: 22px; border-radius: 12px; box-shadow: var(--shadow-sm); }
  .pglyph {
    width: 26px;
    height: 26px;
    border-radius: 4px;
    background: var(--bg-tertiary);
    border: 1px solid var(--border-default);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    font-size: 13px;
    flex-shrink: 0;
    color: var(--text-secondary);
  }
  .authdivider {
    display: flex;
    align-items: center;
    gap: 12px;
    color: var(--text-muted);
    font-size: 11.5px;
    white-space: nowrap;
  }
  .authdivider::before,
  .authdivider::after { content: ''; flex: 1; height: 1px; background: var(--border-default); }
  .mailmock {
    border: 1px dashed var(--border-strong);
    border-radius: 6px;
    padding: 14px;
    background: var(--bg-secondary);
  }

  /* ---------- settings ---------- */
  .setnav-item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 10px;
    border-radius: 6px;
    font-size: 13px;
    font-weight: 500;
    color: var(--text-secondary);
    cursor: pointer;
  }
  .setnav-item:hover { background: var(--hover); color: var(--text-primary); }
  .setnav-item.on { background: color-mix(in srgb, var(--accent) 12%, transparent); color: var(--accent); }
  .setcard .setbody { padding: 16px 18px; }
  .idrow {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 11px 0;
    border-bottom: 1px solid var(--border-subtle);
  }
  .idrow:last-child { border-bottom: none; padding-bottom: 2px; }
  .idrow:first-child { padding-top: 2px; }
  .switchrow {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 12px 0;
    border-bottom: 1px solid var(--border-subtle);
    cursor: pointer;
  }
  .switchrow:last-child { border-bottom: none; padding-bottom: 4px; }
  .switchrow:first-child { padding-top: 4px; }
  .switch {
    width: 36px;
    height: 21px;
    border-radius: 999px;
    background: var(--bg-tertiary);
    border: 1px solid var(--border-strong);
    position: relative;
    flex-shrink: 0;
    transition: background 0.15s, border-color 0.15s;
  }
  .switch .knob {
    position: absolute;
    top: 2px;
    left: 2px;
    width: 15px;
    height: 15px;
    border-radius: 50%;
    background: var(--text-muted);
    transition: transform 0.15s ease, background 0.15s;
  }
  .switch.on { background: var(--accent); border-color: var(--accent); }
  .switch.on .knob { transform: translateX(15px); background: #fff; }

  /* ---------- markdown (shared by releases / issues / readme) ---------- */
  .md { font-size: 14.5px; line-height: 1.7; color: var(--text-primary); }
  .md > *:first-child { margin-top: 0; }
  .md h1 {
    font-family: var(--font-display);
    font-size: 26px;
    font-weight: 700;
    letter-spacing: -0.02em;
    margin: 28px 0 14px;
    padding-bottom: 10px;
    border-bottom: 1px solid var(--border-default);
  }
  .md h2 {
    font-family: var(--font-display);
    font-size: 20px;
    font-weight: 700;
    letter-spacing: -0.01em;
    margin: 26px 0 12px;
    padding-bottom: 8px;
    border-bottom: 1px solid var(--border-subtle);
  }
  .md h3 { font-size: 16px; font-weight: 600; margin: 22px 0 10px; }
  .md p { margin: 0 0 14px; }
  .md a { color: var(--accent); }
  .md a:hover { text-decoration: underline; }
  .md ul, .md ol { margin: 0 0 14px; padding-left: 24px; }
  .md li { margin: 4px 0; }
  .md code {
    font-family: var(--font-mono);
    font-size: 12.5px;
    background: var(--code-bg);
    padding: 0.15em 0.4em;
    border-radius: 4px;
  }
  .md pre {
    background: var(--code-bg);
    border: 1px solid var(--border-default);
    border-radius: 6px;
    padding: 14px 16px;
    overflow: auto;
    margin: 0 0 16px;
  }
  .md pre code { background: none; padding: 0; font-size: 12.5px; line-height: 1.6; }
`;
